import http from "@/helper/http";
import api from "@/helper/api";

function dataEnvelope(response) {
  const data = response?.data?.data;
  if (!data || typeof data !== "object") {
    throw new Error("Invalid report response envelope.");
  }
  return data;
}

export async function fetchInsightsCatalog(signal) {
  const data = dataEnvelope(await http.get(api.insights_catalog, { signal }));
  if (!Array.isArray(data.datasets) || !data.catalog_version) {
    throw new Error("Invalid Insights catalog.");
  }
  return data;
}

export function insightsCatalogForDesigner(catalog) {
  return {
    catalog_version: catalog.catalog_version,
    capabilities: {
      report_definitions: true,
      multi_store: true,
      entity_resolution: true,
    },
    datasets: catalog.datasets.map((dataset) => {
      const fields = [...dataset.dimensions, ...dataset.measures].map(
        (field) => ({ ...field, available: true }),
      );
      return {
        ...dataset,
        fields,
        default_columns: [
          ...dataset.dimensions.slice(0, 2).map((field) => field.id),
          ...dataset.measures.slice(0, 1).map((field) => field.id),
        ],
        category: dataset.category || "Insights",
        scheduled_period_modes: [{ id: "ALL", label: "All selected dates" }],
      };
    }),
  };
}

export async function executeInsightsExplore(payload, signal) {
  const response = await http.post(api.insights_explore, payload, { signal });
  const data = dataEnvelope(response);
  return response.status === 202
    ? { kind: "task", task: data }
    : { kind: "result", result: data };
}

export async function listInsightsDefinitions(signal) {
  const data = dataEnvelope(
    await http.get(api.insights_definitions, { signal }),
  );
  if (!Array.isArray(data.definitions)) {
    throw new Error("Invalid Insights definitions response.");
  }
  return data.definitions;
}

export async function getInsightsDefinition(definitionId, signal) {
  const data = dataEnvelope(
    await http.get(api.insights_definition(definitionId), { signal }),
  );
  return data.definition;
}

export async function createInsightsDefinition(payload) {
  const data = dataEnvelope(await http.post(api.insights_definitions, payload));
  return data.definition;
}

export async function updateInsightsDefinition(definitionId, payload) {
  const data = dataEnvelope(
    await http.patch(api.insights_definition(definitionId), payload),
  );
  return data.definition;
}

export function definitionSaveError(error) {
  if (error?.response?.data?.error_code === "revision_conflict") {
    const current = error.response.data.data?.current_revision;
    return current
      ? `This view changed elsewhere. Current revision: ${current}. Reload it before saving.`
      : "This view changed elsewhere. Reload it before saving.";
  }
  return (
    error?.response?.data?.error?.message ||
    "The Insights view could not be saved."
  );
}

export async function deleteInsightsDefinition(definitionId) {
  return dataEnvelope(
    await http.delete(api.insights_definition(definitionId)),
  );
}

export async function executeInsightsDefinition(definitionId, format = "JSON") {
  const response = await http.post(
    api.insights_definition_execute(definitionId),
    { format },
  );
  return dataEnvelope(response);
}

export async function fetchInsightsTask(taskRecordId, signal) {
  const data = dataEnvelope(
    await http.get(api.insights_task(taskRecordId), { signal }),
  );
  return data.task;
}

export async function fetchInsightsTaskArtifact(taskRecordId, descriptor, signal) {
  const response = await http.get(
    api.insights_task_file(taskRecordId, descriptor.filename),
    {
      signal,
      responseType:
        descriptor.content_type === "application/json" ? "json" : "blob",
    },
  );
  return response.data;
}
